import React, { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardCheck, Check } from "lucide-react";
import api from "../lib/api";
import { useFirma, currentTurno } from "../context/FirmaContext";

const TURNO_LABEL = { mattina: "Mattina", pomeriggio: "Pomeriggio", notte: "Notte" };
const ymd = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

export const CoperturaTurno = ({ confirmati = 0, attesi = 0, testid = "copertura-turno" }) => {
  if (!attesi) return <span data-testid={testid} className="text-[11px] text-muted-foreground">Nessun turno pianificato</span>;
  const pct = Math.round((confirmati / attesi) * 100);
  const color = pct >= 100 ? "#16a34a" : pct > 0 ? "#d97706" : "#dc2626";
  const bg = pct >= 100 ? "#dcfce7" : pct > 0 ? "#fef3c7" : "#fee2e2";
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <div className="h-1.5 flex-1 min-w-[40px] rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
      </div>
      <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap" style={{ background: bg, color }}>{confirmati}/{attesi} · {pct}%</span>
    </div>
  );
};

export const PresaVisioneBanner = ({ tipo, titolo, descrizione, structureId }) => {
  const qc = useQueryClient();
  const { firmaEnabled, identity, mustUnlock } = useFirma();
  const turno = currentTurno();
  const today = ymd();
  const params = { tipo, ...(structureId && structureId !== "all" ? { structure_id: structureId } : {}) };
  const { data: acks = [], isFetched } = useQuery({
    queryKey: ["pv-ack", tipo, structureId],
    refetchInterval: 30000,
    queryFn: async () => { try { return (await api.get("/dashboard/presa-visione", { params })).data; } catch { return []; } },
  });
  const mine = acks.find((a) => a.operatore_id === identity?.operatore_id && a.turno === turno && a.data === today);
  const didRef = useRef(null);
  useEffect(() => {
    if (!firmaEnabled || !identity?.operatore_id || mustUnlock) return;
    if (!isFetched || mine || didRef.current === identity.operatore_id) return;
    didRef.current = identity.operatore_id;
    api.post("/dashboard/presa-visione", { operatore_id: identity?.operatore_id, operatore_nome: identity?.operatore_nome, turno, structure_id: structureId, tipo })
      .then(() => { qc.invalidateQueries({ queryKey: ["pv-ack", tipo] }); qc.invalidateQueries({ queryKey: ["presa-visione-riep"] }); qc.invalidateQueries({ queryKey: ["solleciti-miei"] }); qc.invalidateQueries({ queryKey: ["cosa-manca"] }); })
      .catch(() => { didRef.current = null; });
  }, [firmaEnabled, mustUnlock, identity?.operatore_id, isFetched, mine, tipo, structureId, turno, qc]);
  if (!firmaEnabled) return null;
  return (
    <>
      <div className="gr-card p-4 mb-6 hidden md:flex items-center gap-3 flex-wrap" data-testid={`presa-visione-box-${tipo}`} style={{ borderLeft: "4px solid var(--gr-primary,#1f7a5c)" }}>
        <ClipboardCheck size={20} className="text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-head font-bold text-sm">{titolo} · turno {TURNO_LABEL[turno]}</div>
          <div className="text-xs text-muted-foreground">{descrizione}</div>
        </div>
        {mine
          ? <span className="text-sm font-semibold text-emerald-700 inline-flex items-center gap-1.5" data-testid={`presa-visione-ok-${tipo}`}><Check size={16} /> Presa visione registrata alle {new Date(mine.ts).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>
          : <span className="text-xs text-muted-foreground italic" data-testid={`presa-visione-auto-${tipo}`}>Registrazione automatica…</span>}
      </div>
      <div className="md:hidden mb-3" data-testid={`presa-visione-mobile-${tipo}`}>
        {mine
          ? <span className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1" data-testid={`presa-visione-ok-m-${tipo}`}><Check size={13} /> Presa visione registrata alle {new Date(mine.ts).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>
          : <span className="text-[11px] text-muted-foreground italic">Registrazione automatica…</span>}
      </div>
    </>
  );
};
